import type { PlanType } from "../../@types";
import { useMctFlags } from "../flag-hooks/useMctFlags";
import { useLoggedIn } from "../loginHelpers";
import { usePlanType } from "../planHelpers";
import { isMedicareAdvantageType } from "../planTypes";
import { useAppState } from "./useAppState";

/**
 * Hook which returns value of provider network flag, unless plan type is not a
 * Medicare Advantage type, in which case it will return false.
 */
export const useProviderNetwork = (
  planType?: PlanType
): { enableProviderNetwork: boolean } => {
  const { tmpFeEnableProviderNetwork, tmpFeProviderNetworkDisableYear } =
    useMctFlags();
  const isLoggedIn = useLoggedIn();
  // `tmpFeProviderNetworkDisableYear` should always be a string, but adding
  // this is an extra safety, and means tests don't need to mock the flag with
  // an empty string. Casting here so the comparison with `year`, below, is clearer
  const disabledYear = tmpFeProviderNetworkDisableYear as
    typeof tmpFeProviderNetworkDisableYear | undefined;
  const { year } = useAppState();
  const statePlanType = usePlanType();
  const effectivePlanType = planType ?? statePlanType;

  const enableForPlanType = effectivePlanType
    ? isMedicareAdvantageType(effectivePlanType)
    : isLoggedIn || !!(statePlanType && isMedicareAdvantageType(statePlanType));

  const enableProviderNetwork =
    !!tmpFeEnableProviderNetwork && // cast to boolean to avoid issues with tests if flag is undefined
    (disabledYear === undefined || disabledYear !== year) &&
    enableForPlanType;

  return {
    enableProviderNetwork,
  };
};
